import { useCallback, useEffect, useMemo, useRef } from 'react'
import * as isomorphicSessionStorage from 'lib/storage/isomorphicSessionStorage'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'

const EXIT_INTENT_SHOWN_KEY = 'exitIntentModalShown'

// Desktop: 3 minutes of inactivity
const DESKTOP_INACTIVITY_THRESHOLD_MS = 3 * 60 * 1000

// Mobile: 15 seconds of inactivity on first page view
const MOBILE_INACTIVITY_THRESHOLD_MS = 15 * 1000

// Events that indicate user activity
const WINDOW_ACTIVITY_EVENTS = ['scroll', 'resize'] as const
const DOCUMENT_ACTIVITY_EVENTS = [
  'wheel', 'keydown', 'keyup', 'mousedown', 'mousemove',
  'touchstart', 'touchmove', 'click', 'contextmenu',
] as const

interface UseExitIntentOptions {
  enabled?: boolean
  onExitIntent?: () => void
}

/**
 * Hook to detect user inactivity and trigger exit intent callback.
 *
 * Desktop: Triggers after 3 minutes of inactivity
 * Mobile: Triggers after 15 seconds of inactivity on first page view
 *
 * Callback is only triggered once per session.
 */
function useExitIntent(options: UseExitIntentOptions = {}): void {
  const { enabled = false, onExitIntent } = options

  const isMobileDevice = useIsMobileDevice()
  const hasTriggeredRef = useRef(false)
  const idleTimeoutRef = useRef<number | undefined>(undefined)
  const lastActivityRef = useRef(Date.now())
  const onExitIntentRef = useRef(onExitIntent)

  // Keep callback ref updated
  onExitIntentRef.current = onExitIntent

  // Check if modal was already shown this session
  const wasAlreadyShown = useMemo((): boolean => {
    return isomorphicSessionStorage.get(EXIT_INTENT_SHOWN_KEY) === true
  }, [])

  // Mark modal as shown for this session
  const markAsShown = useCallback(() => {
    isomorphicSessionStorage.set(EXIT_INTENT_SHOWN_KEY, true)
  }, [])

  // Handle idle state - trigger the callback
  const handleIdle = useCallback(() => {
    if (hasTriggeredRef.current || wasAlreadyShown) {
      return
    }

    hasTriggeredRef.current = true
    markAsShown()
    onExitIntentRef.current?.()
  }, [wasAlreadyShown, markAsShown])

  // Handle user activity - reset the idle timer
  const handleActivity = useCallback(() => {
    const now = Date.now()

    // Throttle: ignore activity within 500ms of last handled activity
    if (now - lastActivityRef.current < 500) {
      return
    }
    lastActivityRef.current = now

    // Don't reset if already triggered
    if (hasTriggeredRef.current) {
      return
    }

    // Reset the idle timeout
    window.clearTimeout(idleTimeoutRef.current)

    const threshold = isMobileDevice
      ? MOBILE_INACTIVITY_THRESHOLD_MS
      : DESKTOP_INACTIVITY_THRESHOLD_MS

    idleTimeoutRef.current = window.setTimeout(handleIdle, threshold)
  }, [isMobileDevice, handleIdle])

  useEffect(() => {
    // Don't run if disabled or already shown this session
    if (!enabled || wasAlreadyShown) {
      return
    }

    // Set up event listeners for activity detection
    const windowOptions: AddEventListenerOptions = { passive: true, capture: true }
    const documentOptions: AddEventListenerOptions = { passive: true }

    WINDOW_ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, windowOptions)
    })
    DOCUMENT_ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, documentOptions)
    })

    // Start the initial idle timeout
    const threshold = isMobileDevice
      ? MOBILE_INACTIVITY_THRESHOLD_MS
      : DESKTOP_INACTIVITY_THRESHOLD_MS

    idleTimeoutRef.current = window.setTimeout(handleIdle, threshold)

    return () => {
      WINDOW_ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity, windowOptions)
      })
      DOCUMENT_ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity)
      })
      window.clearTimeout(idleTimeoutRef.current)
    }
  }, [enabled, isMobileDevice, handleActivity, handleIdle, wasAlreadyShown])
}

export default useExitIntent
